import { isRepaymentSettled } from "../utils/repaymentStatus";
import React, { useEffect, useMemo, useState } from "react";
import KPIChartCard from "./KPIChartCard";

const money = (n, compact = false) => {
  if (!Number.isFinite(+n)) return "—";
  const value = +n;
  if (!compact) {
    return value.toLocaleString("en-US", {
      style: "currency",
      currency: "SGD",
    });
  }
  return new Intl.NumberFormat("en-SG", {
    style: "currency",
    currency: "SGD",
    notation: "compact",
    compactDisplay: "short",
    maximumFractionDigits: 1,
  }).format(value);
};

export default function HalfPieChartBorrower({ alltheFreakingLoanData }) {
  const getIsMobile = () =>
    typeof window !== "undefined" ? window.innerWidth <= 1200 : false;
  const [isMobile, setIsMobile] = useState(getIsMobile());

  useEffect(() => {
    const handleResize = () => setIsMobile(getIsMobile());
    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  const m = useMemo(() => {
    const loans = Array.isArray(alltheFreakingLoanData)
      ? alltheFreakingLoanData
      : [];

    let paid = 0;
    let total = 0;

    for (const loan of loans) {
      for (const r of loan?.remaining || []) {
        const amt = +r.repay_amount || 0;
        total += amt;
        if (isRepaymentSettled(r)) paid += amt;
      }
    }

    const outstanding = Math.max(0, total - paid);
    const pctRaw = total > 0 ? (paid / total) * 100 : 0;
    const pct = Math.max(0, Math.min(100, Math.round(pctRaw)));

    return {
      total,
      paid,
      outstanding,
      pct,
      isEmpty: total <= 0,
    };
  }, [alltheFreakingLoanData]);

  // --- SVG ring math (semi-circle) ---
  const size = isMobile ? 100 : 120; // viewBox size
  const cx = size / 2;
  const cy = size / 2 + (isMobile ? 8 : 10); // push down a bit so the arc sits nicely
  const r = isMobile ? 34 : 42;
  const stroke = isMobile ? 10 : 12;

  const circumference = 2 * Math.PI * r;
  // show only half ring: use dasharray for half, hide other half
  const half = circumference / 2;
  const progress = (m.pct / 100) * half;

  return (
    <KPIChartCard
      title="Total Loaned Amount Paid"
      pill="Includes assumed paid"
      value={money(m.paid, isMobile)}
      subtitle={m.isEmpty || isMobile ? "" : `Out of ${money(m.total)}`}
      footer={m.isEmpty || isMobile ? "" : `${m.pct}% paid`}
      isEmpty={m.isEmpty}
      emptyTitle="No repayments yet"
      emptySub="Once you start paying, you’ll see paid vs outstanding here."
    >
      <div
        style={{
          width: "100%",
          height: "100%",
          display: "grid",
          gridTemplateColumns: isMobile ? "1fr" : "140px minmax(0, 1fr)",
          gap: isMobile ? 8 : 12,
          alignItems: "center",
          justifyItems: isMobile ? "center" : "stretch",
        }}
      >
        {/* Ring */}
        <div
          style={{
            position: "relative",
            width: isMobile ? 110 : 140,
            height: isMobile ? 90 : 110,
          }}
        >
          <svg
            width={isMobile ? 110 : 140}
            height={isMobile ? 90 : 110}
            viewBox={`0 0 ${size} ${size}`}
            style={{ display: "block" }}
          >
            {/* Track (half) */}
            <circle
              cx={cx}
              cy={cy}
              r={r}
              fill="none"
              stroke="var(--br-soft, rgba(148,163,184,0.30))"
              strokeWidth={stroke}
              strokeLinecap="round"
              strokeDasharray={`${half} ${circumference}`}
              strokeDashoffset={half * 0.05} // tiny shift so ends look nicer
              transform={`rotate(180 ${cx} ${cy})`}
            />

            {/* Progress (half) */}
            <circle
              cx={cx}
              cy={cy}
              r={r}
              fill="none"
              stroke="var(--br-primary, #7057f6)"
              strokeWidth={stroke}
              strokeLinecap="round"
              strokeDasharray={`${progress} ${circumference}`}
              strokeDashoffset={half * 0.05}
              transform={`rotate(180 ${cx} ${cy})`}
            />

            {/* soft highlight */}
            <circle
              cx={cx}
              cy={cy}
              r={r}
              fill="none"
              stroke="rgba(255,255,255,0.6)"
              strokeWidth={2}
              strokeDasharray={`${half} ${circumference}`}
              strokeDashoffset={half * 0.05}
              transform={`rotate(180 ${cx} ${cy}) translate(0, 1)`}
            />
          </svg>

          {/* Center % badge */}
          <div
            style={{
              position: "absolute",
              left: "50%",
              top: "55%",
              transform: "translate(-50%, -50%)",
              width: isMobile ? 50 : 58,
              height: isMobile ? 34 : 38,
              borderRadius: 999,
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              fontFamily: "Manrope, sans-serif",
              fontWeight: 900,
              fontSize: isMobile ? 12 : 14,
              color: "var(--br-text, rgba(15,23,42,0.82))",
              background: "var(--br-surface, rgba(255,255,255,0.85))",
              border: "1px solid var(--br-border, rgba(148,163,184,0.22))",
              boxShadow: "0 10px 18px rgba(15,23,42,0.08)",
            }}
          >
            {m.pct}%
          </div>
        </div>

        {/* Legend + mini stats */}
        {!isMobile ? (
          <div
            style={{
              width: "100%",
              display: "flex",
              flexDirection: "column",
              gap: 8,
            }}
          >
            <div
              style={{
                display: "flex",
                gap: 10,
                flexWrap: "wrap",
                fontFamily: "Manrope, sans-serif",
                fontWeight: 800,
                fontSize: 12,
                color: "var(--br-muted, rgba(15,23,42,0.55))",
              }}
            >
              <span
                style={{
                  display: "inline-flex",
                  alignItems: "center",
                  gap: 6,
                }}
              >
                <span
                  style={{
                    width: 10,
                    height: 10,
                    borderRadius: 999,
                    background: "var(--br-primary, #7057f6)",
                    display: "inline-block",
                  }}
                />
                Paid
              </span>

              <span
                style={{
                  display: "inline-flex",
                  alignItems: "center",
                  gap: 6,
                }}
              >
                <span
                  style={{
                    width: 10,
                    height: 10,
                    borderRadius: 999,
                    background: "var(--br-soft, rgba(148,163,184,0.35))",
                    display: "inline-block",
                  }}
                />
                Outstanding
              </span>
            </div>

            <div
              style={{
                display: "grid",
                gridTemplateColumns: "minmax(0, 1fr)",
                gap: 10,
              }}
            >
              <div
                style={{
                  borderRadius: 14,
                  border: "1px solid var(--br-border, rgba(148,163,184,0.18))",
                  background: "rgba(255,255,255,0.55)",
                  padding: "6px 8px",
                }}
              >
                <div
                  style={{
                    fontFamily: "Manrope, sans-serif",
                    fontWeight: 900,
                    fontSize: 12,
                    color: "var(--br-muted, rgba(15,23,42,0.55))",
                  }}
                >
                  Paid
                </div>
                <div
                  style={{
                    fontFamily: "Manrope, sans-serif",
                    fontWeight: 900,
                    fontSize: 13,
                    color: "var(--br-text, rgba(15,23,42,0.80))",
                    marginTop: 2,
                  }}
                >
                  {money(m.paid)}
                </div>
              </div>

              <div
                style={{
                  borderRadius: 14,
                  border: "1px solid var(--br-border, rgba(148,163,184,0.18))",
                  background: "rgba(255,255,255,0.55)",
                  padding: "6px 8px",
                }}
              >
                <div
                  style={{
                    fontFamily: "Manrope, sans-serif",
                    fontWeight: 900,
                    fontSize: 12,
                    color: "var(--br-muted, rgba(15,23,42,0.55))",
                  }}
                >
                  Outstanding
                </div>
                <div
                  style={{
                    fontFamily: "Manrope, sans-serif",
                    fontWeight: 900,
                    fontSize: 13,
                    color: "var(--br-text, rgba(15,23,42,0.80))",
                    marginTop: 2,
                  }}
                >
                  {money(m.outstanding)}
                </div>
              </div>
            </div>
          </div>
        ) : null}
      </div>
    </KPIChartCard>
  );
}
